<template>
  <section
    v-if="data"
    class="relative"
    :style="{
      '--backgroundColor': tagColorStyles.background,
      '--textColor': tagColorStyles.text,
    }"
  >
    <blog-reading-progress-bar />

    <header>
      <div class="bg-[--backgroundColor] pt-4 text-[--textColor]">
        <app-page-wrapper class="mb-8">
          <app-breadcrumb class="max-w-[400px]" :items="breadcrumb" />
        </app-page-wrapper>

        <app-page-wrapper :class="pageWrapperClasses">
          <div class="max-w-[900px]">
            <h1
              class="balance mb-4 font-title text-2xl/relaxed lg:text-4xl/relaxed"
            >
              {{ data.title }}
            </h1>

            <blog-post-information :post="data" />
          </div>
        </app-page-wrapper>
      </div>
    </header>

    <div
      class="bg-gradient-to-b from-[--backgroundColor] from-[200px] to-white to-[200px]"
    >
      <app-page-wrapper
        class="grid gap-12 pb-12 pt-8 lg:grid-cols-[1fr_350px]"
        :class="pageWrapperClasses"
      >
        <div>
          <app-image
            alt=""
            class="aspect-[1.8] w-full rounded rounded-br-[7vw] object-cover"
            :src="data.og_image"
            :width="1920"
          />
          <div
            class="prose mt-12 max-w-[800px] text-black"
            v-html="data.html"
          />
        </div>

        <div>
          <div class="sticky-below-header grid gap-8">
            <blog-post-resources
              v-if="data.resources.length > 0"
              :post="data"
            />

            <blog-post-related-course
              v-if="relatedCourse"
              :course="relatedCourse"
            />
          </div>
        </div>
      </app-page-wrapper>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { useBlogTagColors } from "~/business-areas/blog/composables/blog-tag-colors.hook";
import BlogPostInformation from "~/pages/blog/_includes/components/blog-post-information.vue";
import BlogPostRelatedCourse from "~/pages/blog/_includes/components/blog-post-related-course.vue";
import BlogPostResources from "~/pages/blog/_includes/components/blog-post-resources.vue";
import { useBlogPostRelatedCourse } from "~/pages/blog/_includes/composables/blog-post-related-course.hook";
import type { BreadcrumbItem } from "~/shared/breadcrumb/breadcrumb.model";

definePageMeta({
  noFaq: true,
});

const slug = useRoute("blog-slug").params.slug;
const { t } = useI18n();
const { data } = await useFetch(`/api/blog/posts/${slug}`);

if (!data.value)
  throw createError({
    statusCode: 404,
    fatal: true,
    statusMessage: t("blog.post.not_found_error"),
  });

if (data.value.slug !== slug) {
  await navigateTo(
    { name: "blog-slug", params: { slug: data.value.slug } },
    { redirectCode: 301 },
  );
}

const tagColorStyles = useBlogTagColors(data.value?.tags?.[0]);

const pageWrapperClasses = "!max-w-[1300px]";

const { relatedCourse } = useBlogPostRelatedCourse(data.value);

const breadcrumb: BreadcrumbItem[] = [
  {
    label: "Tous les articles",
    to: "/blog",
  },
  {
    label: data.value.title!,
    to: { name: "blog-slug", params: { slug: data.value.slug } },
  },
];

useSchemaOrg([
  defineArticle({
    "@type": "BlogPosting",
    image: data.value.og_image,
    datePublished: data.value.created_at,
  }),
]);

useSeoMeta({
  title: () => data.value!.title!,
  description: () => data.value!.metadescription,
  ogImage: () => data.value!.og_image,
});

defineAnalyticsContext({
  post: {
    id: slug,
    title: data.value!.title,
    resources: data.value!.resources,
  },
});

const viewResourceId = useRoute().query.view_resource as string | undefined;
if (import.meta.client && viewResourceId) {
  useBlogPostResourceViewer({
    postId: data.value.id,
    resourceId: viewResourceId,
  }).view();
} else if (import.meta.client) {
  /*
  Only tracking post view in that case to avoid
  doubled view when user is redirected back to blog post after contact page
  when accessing an attached resource.
   */
  useNuxtApp().$analytics.track("blog_post_viewed");
}
</script>
